import { Button, Flex, Input, Menu, MenuContent, MenuTrigger, Text } from '@optiaxiom/react';
import { IconSearch, IconXmark } from '@optimizely/axiom-icons';

import { styles } from './InstructionSearchInput.css';

type InstructionSearchInputProps = {
  filterText: string;
  handleFilterChange: (value: string) => void;
  onClickAddInstruction: () => void;
  productFilter: string[];
  statusFilter: string[];
  availableProducts: string[];
  onProductFilterChange: (products: string[]) => void;
  onStatusFilterChange: (statuses: string[]) => void;
};

export function InstructionSearchInput({
  filterText,
  handleFilterChange,
  onClickAddInstruction,
  productFilter,
  statusFilter,
  availableProducts,
  onProductFilterChange,
  onStatusFilterChange,
}: InstructionSearchInputProps) {
  const statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  const handleProductSelect = (product: string) => {
    const newProducts = productFilter.includes(product)
      ? productFilter.filter(p => p !== product)
      : [...productFilter, product];
    onProductFilterChange(newProducts);
  };

  const handleProductSelectAll = () => {
    const allSelected = availableProducts.every(product => productFilter.includes(product));
    if (allSelected) {
      onProductFilterChange([]);
    } else {
      onProductFilterChange([...availableProducts]);
    }
  };

  const handleStatusSelect = (status: string) => {
    const newStatuses = statusFilter.includes(status)
      ? statusFilter.filter(s => s !== status)
      : [...statusFilter, status];
    onStatusFilterChange(newStatuses);
  };

  const handleStatusSelectAll = () => {
    const allSelected = statusOptions.every(status => statusFilter.includes(status.value));
    if (allSelected) {
      onStatusFilterChange([]);
    } else {
      onStatusFilterChange(statusOptions.map(status => status.value));
    }
  };

  const getProductFilterLabel = () => {
    if (productFilter.length === 0) return 'All';
    if (productFilter.length === availableProducts.length) return 'All';
    if (productFilter.length === 1) return productFilter[0];
    return `${productFilter.length} Products`;
  };

  const getStatusFilterLabel = () => {
    if (statusFilter.length === 0) return 'All';
    if (statusFilter.length === statusOptions.length) return 'All';
    if (statusFilter.length === 1) return statusFilter[0] === 'active' ? 'Active' : 'Inactive';
    return 'Both Status';
  };

  return (
    <Flex className={styles.searchInputContainer}>
      <Flex flexDirection="row" gap="8" alignItems="center">
        <Input
          className={styles.searchInput}
          addonBefore={<IconSearch />}
          addonAfter={filterText && <IconXmark onClick={() => handleFilterChange('')} />}
          placeholder="Search instruction name"
          value={filterText}
          onChange={e => handleFilterChange(e.target.value)}
        />

        <Menu
          options={[
            {
              label: 'All',
              execute: handleProductSelectAll,
              selected: availableProducts.every(product => productFilter.includes(product)),
            },
            ...availableProducts.map(product => ({
              label: product,
              execute: () => handleProductSelect(product),
              selected: productFilter.includes(product),
            })),
          ]}
        >
          <MenuTrigger h="40">
            <Text textAlign="start" fontSize="sm">
              Product
            </Text>
            <Text textAlign="start" fontSize="xs">
              {getProductFilterLabel()}
            </Text>
          </MenuTrigger>
          <MenuContent />
        </Menu>

        <Menu
          options={[
            {
              label: 'All',
              execute: handleStatusSelectAll,
              selected: statusOptions.every(status => statusFilter.includes(status.value)),
            },
            ...statusOptions.map(status => ({
              label: status.label,
              execute: () => handleStatusSelect(status.value),
              selected: statusFilter.includes(status.value),
            })),
          ]}
        >
          <MenuTrigger h="40">
            <Flex gap="0">
              <Text textAlign="start" fontSize="sm">
                Status
              </Text>
              <Text textAlign="start" fontSize="xs">
                {getStatusFilterLabel()}
              </Text>
            </Flex>
          </MenuTrigger>
          <MenuContent />
        </Menu>
      </Flex>

      <Button appearance="primary" size="md" onClick={onClickAddInstruction}>
        Add instruction
      </Button>
    </Flex>
  );
}
